import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { AnimatePresence, motion } from "framer-motion";
import { FiCheck, FiArrowRight, FiAlertCircle, FiFileText, FiMoon, FiSun } from "react-icons/fi";
import TextInput from "./formComp/TextInputNew";
import { SERVICE_ICONS } from "./formComp/formIcons";
import {
  DigitalModelForm,
  NightguardForm,
  OtherTreatmentsForm,
  SmileDesignForm,
  SurgicalGuideForm,
  VeneersForm,
  CompleteDentureForm,
  PartialDentureForm,
  PartialCastForm,
} from "../../types/UploadFieldsType";
import { CrownBridgeOutput, ImplantOutput } from "../../types/BlueCardTypes";
import CrownBridge from "./userCaseUpload/CrownBridgeNew";
import Implant from "./userCaseUpload/ImplantNew";
import SmileDesign from "./userCaseUpload/SmileDesignNew";
import Veneers from "./userCaseUpload/VeneersNew";

import CompleteDenture from "./userCaseUpload/CompletDentureNew";
import PartialDenture from "./userCaseUpload/PartialDentureNew";
import PartialCast from "./userCaseUpload/PartialCastNew";

import SurgicalGuide from "./userCaseUpload/SurgicalGuideNew";
import NightGuard from "./userCaseUpload/NightGuardNew";
import DigitalModel from "./userCaseUpload/DigitalModelNew";
import OtherTreatments from "./userCaseUpload/OtherTreatmentsNew";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { uploadCase } from "../../store/user/userSlice";
import { UploadFormData } from "../../types/UploadFormTypes";
import { useToast } from "../utilities/Toast";
import SelectInput from "./formComp/SelectInputNew";
import { FormValidationProvider } from "./formComp/FormValidationContext";
import { useNavigate } from "react-router-dom";
import { isVelvetLabToken } from "../../utils/brandLogo";
import { isFzToken } from "../../utils/fzLab";

const formContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.05 },
  },
};

const formSection = {
  hidden: { opacity: 0, y: 18 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
  },
};

const servicePanel = {
  initial: { opacity: 0, y: 14 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] },
  },
  exit: {
    opacity: 0,
    y: -8,
    transition: { duration: 0.22 },
  },
};

export type CreatedPortalCase = {
  case_id: string;
  case_owner: string;
  box_folder_id?: number | null;
};

export type CaseDetailsFormEmbedProps = {
  /** Stay on the current page after submit instead of opening the upload route */
  embedded?: boolean;
  /** Prefill patient name; the client can still edit it */
  initialPatientName?: string;
  /** Lab token taken from the smile case id; not shown on the form */
  initialUploadToken?: string;
  /** Smile case this cases-portal case should stay linked to */
  smileId?: string;
  /** Preselect one service and keep the category locked */
  lockedServiceKey?: "smileDesign";
  forceTheme?: "dark" | "light";
  /** Uris clients use the dark red case form */
  brand?: "uris";
  onCaseCreated?: (created: CreatedPortalCase) => void;
};

export const CaseDetailsForm = ({
  embedded = false,
  initialPatientName = "",
  initialUploadToken = "",
  smileId = "",
  lockedServiceKey,
  forceTheme,
  brand,
  onCaseCreated,
}: CaseDetailsFormEmbedProps = {}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const patientTouched = useRef(false);
  const { user } = useAppSelector((state) => state.auth);
  const { employee } = useAppSelector((state) => state.allocationAuth);
  const { showToast } = useToast();
  const user_id = user?.user_id;

  const [formData, setFormData] = useState<UploadFormData>({
    case_owner: initialPatientName,
    upload_token: initialUploadToken,
    service_type: lockedServiceKey || "",
    details_json: {},
  });

  // const [splintedCrowns, setSplintedCrowns] = useState("");
  const [additionalNote, setAdditionalNote] = useState("");
  const [previewNeeded, setPreviewNeeded] = useState("");
  const [orderFormConfirmed, setOrderFormConfirmed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [formTheme, setFormTheme] = useState<"dark" | "light">(() => {
    if (forceTheme) return forceTheme;
    try {
      const saved = localStorage.getItem("caseUploadTheme");
      return saved === "light" || saved === "dark" ? saved : "dark";
    } catch {
      return "dark";
    }
  });

  useEffect(() => {
    if (!initialPatientName || patientTouched.current) return;
    setFormData((prev) =>
      prev.case_owner === initialPatientName
        ? prev
        : { ...prev, case_owner: initialPatientName }
    );
  }, [initialPatientName]);

  useEffect(() => {
    if (!initialUploadToken) return;
    setFormData((prev) =>
      prev.upload_token === initialUploadToken
        ? prev
        : { ...prev, upload_token: initialUploadToken }
    );
  }, [initialUploadToken]);

  const toggleFormTheme = () => {
    setFormTheme((prev) => {
      const next = prev === "dark" ? "light" : "dark";
      try {
        localStorage.setItem("caseUploadTheme", next);
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  // NEW STATE FOR CASE PRIORITY
  const [casePriority, setCasePriority] = useState(
    "Medium priority (Regular case)"
  );

  const [crownBridgeData, setCrownBridgeData] = useState<CrownBridgeOutput>({
    occlusalContact: "",
    proximalContact: "",
    ponticDesign: "",
    instanceDetails: [],
  });

  const [implantData, setImplantData] = useState<ImplantOutput>({
    occlusalContact: "",
    proximalContact: "",
    ponticDesign: "",
    instanceDetails: [],
  });

  const [smileDesignData, setSmileDesignData] = useState<SmileDesignForm>({
    toothNumbers: [],
    photoIncluded: embedded ? "Yes" : "",
    biteOpening: "",
    teethExtractions: [],
    waxUp: "",
    proposedTreatmentPlan: "",
    otherConsiderations: "",
  });

  const [veneersData, setVeneersData] = useState<VeneersForm>({
    toothNumbers: [],
    photoIncluded: "",
    preparation: "",
    mergedModelNeeded: "",
    placementTag: "",
    changeInVerticalDimention: "",
    otherConsiderations: "",
  });

  const [completeDentureData, setcompleteDentureData] =
    useState<CompleteDentureForm>({
      teethReplaced: [],
      occlusionType: "",
      desiredOutput: "",
      patientGender: "",
      patientAgeGroup: "",
      finishedProduct: "",
      modelNeeded: "",
      shade: "",
      additionalNotes: "",
    });
  const [partialDentureData, setpartialDentureData] =
    useState<PartialDentureForm>({
      teethReplaced: [],
      teethExtracted: [],
      desiredOutput: "",
      finishedProduct: "",
      modelNeeded: "",
      additionalNotes: "",
      shade: "",
    });
  const [partialCastData, setpartialCastData] = useState<PartialCastForm>({
    frameworkMaterial: "",
    desiredArch: "",
    majorConnectorUpperDesignType: "",
    majorConnectorUpperCreateWindow: "",
    majorConnectorLower: "",
    minorConnector: "",
    directRetainerTeeth: [],
    directRetainerClasp: "",
    indirectRetainer: "",
    rest: "",
    additionalNotes: "",
  });

  const [surgicalGuideData, setSurgicalGuideData] = useState<SurgicalGuideForm>(
    {
      dicomFile: "",
      stlFile: "",
      plannedImplantSites: [],
      plannedImplantManufacturer: "",
      teethExtractions: [],
      guideType: "",
      otherConsiderations: "",
      pilotDrillSize: "",
    }
  );

  const [nightGuardData, setNightGuardData] = useState<NightguardForm>({
    neededFor: "",
    margin: "",
    occlusalSurface: "",
    thickness: "",
  });

  const [digitalModelData, setDigitalModelData] = useState<DigitalModelForm>({
    neededFor: "",
    modelType: "",
    dieRequirement: "",
    otherPreferences: "",
    otherSpecifications: "",
  });

  const [otherTreatmentsData, setOtherTreatmentsData] =
    useState<OtherTreatmentsForm>({
      requirement: "",
    });

  const services = ["admin", "operations", "super admin"].includes(
    user?.role?.toLowerCase() || ""
  )
    ? [
      "Crown & Bridge",
      "Implant",
      "Smile Design / Mock-up",
      "No-prep Veneers",
      "LDP Complete denture",
      "LDP Partial denture",
      "RPD Framework",
      "Surgical Guide",
      "Nightguard / Bite splint",
      "Digital model",
      "Other treatments",
      "Upload filled order",
    ]
    : user?.partner === "bant"
      ? [
        "Crown & Bridge",
        "Smile Design / Mock-up",
        "Digital model",
        "LDP Complete denture",
        "LDP Partial denture",
        "RPD Framework",
        "Other treatments",
        "Upload filled order",
      ]
      : isVelvetLabToken(user?.token) || isVelvetLabToken(formData.upload_token)
        ? [
          "Crown & Bridge",
          "Implant",
          "Smile Design / Mock-up",
          "No-prep Veneers",
          "Surgical Guide",
          "Nightguard / Bite splint",
          "Digital model",
          "Other treatments",
          "Upload filled order",
        ]
        : [
          "Crown & Bridge",
          "Implant",
          "Smile Design / Mock-up",
          "Surgical Guide",
          "Nightguard / Bite splint",
          "Digital model",
          "Other treatments",
          "Upload filled order",
        ];

  const serviceMap: Record<string, string> = {
    "Crown & Bridge": "crownAndBridge",
    Implant: "Implant",
    "Smile Design / Mock-up": "smileDesign",
    "No-prep Veneers": "veneers",
    "LDP Complete denture": "complete-denture",
    "LDP Partial denture": "partial-denture",
    "RPD Framework": "partial-cast",
    "Surgical Guide": "surgicalGuide",
    "Nightguard / Bite splint": "nightguard-bite-splint",
    "Digital model": "digital-model",
    "Other treatments": "otherTreatments",
    "Upload filled order": "filledOrder",
  };

  const selectedServices = formData.service_type
    ? formData.service_type.split(",").map((s) => s.trim())
    : [];

  const handleCheckboxChange = (service: string) => {
    if (lockedServiceKey) return;
    const key = serviceMap[service];
    if (key === "filledOrder") {
      const newState = !orderFormConfirmed;
      setOrderFormConfirmed(newState);
      setFormData((prev) => ({
        ...prev,
        service_type: newState ? "filledOrder" : "",
      }));
    } else {
      setOrderFormConfirmed(false);
      setFormData((prev) => {
        const current = prev.service_type
          ? prev.service_type
            .split(",")
            .filter((s) => s.trim() && s !== "filledOrder")
          : [];

        let updated: string[];
        if (current.includes(key)) {
          updated = current.filter((s) => s !== key);
        } else {
          updated = [...current, key];
        }

        return {
          ...prev,
          service_type: updated.join(","),
        };
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === "case_owner") {
      patientTouched.current = true;
    }

    if (name === "upload_token") {
      const sanitized = value
        .replace(/[^A-Za-z]/g, "") // remove numbers, spaces, specials
        .toUpperCase()
        .slice(0, 2); // max 2 characters

      setFormData((prev) => ({
        ...prev,
        upload_token: sanitized,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFormKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    const target = e.target as HTMLElement;
    if (e.key === "Enter" && target.tagName !== "TEXTAREA") {
      e.preventDefault();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (
      isFzToken(user?.token) ||
      isFzToken(formData.upload_token)
    ) {
      showToast(
        "FZ lab cases cannot be created from the portal. Use Local Downloader → FZ Sync.",
        "error",
      );
      return;
    }
    // Validate before submitting
    if (!validateRequiredFields()) {
      setShowValidationErrors(true);
      showToast("Please fill all required fields before submitting.", "error");
      // Scroll to first invalid field after paint
      requestAnimationFrame(() => {
        const firstInvalid = document.querySelector<HTMLElement>(
          "[data-invalid='true']"
        );
        firstInvalid?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
      return;
    }
    setShowValidationErrors(false);
    setIsSubmitting(true);

    const details_json: Record<string, any> = {
      patientName: formData.case_owner,
      additionalNote: additionalNote || "",
      previewNeeded: previewNeeded || "",
    };

    if (casePriority) details_json["casePriority"] = casePriority;
    if (orderFormConfirmed) details_json["UploadFilledOrder"] = true;

    // Build the services object
    const services: Record<string, any> = {};

    if (selectedServices.includes("crownAndBridge")) {
      services["crownAndBridge"] = crownBridgeData || {};
    }
    if (selectedServices.includes("Implant")) {
      services["Implant"] = implantData || {};
    }
    if (selectedServices.includes("smileDesign")) {
      services["smileDesign"] = smileDesignData || {};
    }
    if (selectedServices.includes("veneers")) {
      services["veneers"] = veneersData || {};
    }

    if (selectedServices.includes("complete-denture")) {
      services["complete-denture"] = completeDentureData || {};
    }
    if (selectedServices.includes("partial-denture")) {
      services["partial-denture"] = partialDentureData || {};
    }
    if (selectedServices.includes("partial-cast")) {
      services["partial-cast"] = partialCastData || {};
    }

    if (selectedServices.includes("surgicalGuide")) {
      services["surgicalGuide"] = surgicalGuideData || {};
    }
    if (selectedServices.includes("nightguard-bite-splint")) {
      services["nightguard-bite-splint"] = nightGuardData || {};
    }
    if (selectedServices.includes("digital-model")) {
      services["digital-model"] = digitalModelData || {};
    }
    if (selectedServices.includes("otherTreatments")) {
      services["OtherTreatments"] = otherTreatmentsData || {};
    }

    details_json["services"] = services;

    const completeFormData = {
      ...formData,
      user_id,
      details_json: JSON.stringify(details_json),
      createdBy:
        user?.role === "Super Admin"
          ? user?.user_name
          : employee
            ? employee.emp_name
            : "Client",
      ...(smileId ? { smile_id: smileId } : {}),
    };

    const result = await dispatch(uploadCase(completeFormData));
    setIsSubmitting(false);

    if (uploadCase.fulfilled.match(result)) {
      showToast("Case Details uploaded!", "success");
      const created = result.payload as CreatedPortalCase;
      if (embedded && onCaseCreated && created?.case_id) {
        onCaseCreated({
          case_id: created.case_id,
          case_owner: created.case_owner || formData.case_owner,
          box_folder_id: created.box_folder_id ?? null,
        });
      } else {
        navigate(`/dashboard/upload/${created.case_id}`);
      }
    } else {
      showToast("Case Details Upload failed: " + result.payload, "error");
    }
  };

  const REQUIRED_FIELDS_BY_SERVICE: Record<string, string[]> = {
    crownAndBridge: ["instanceDetails"],
    Implant: ["instanceDetails"],
    smileDesign: [
      "toothNumbers",
      "photoIncluded",
      "waxUp",
      "proposedTreatmentPlan",
    ],
    veneers: [
      "toothNumbers",
      "photoIncluded",
      "preparation",
      "mergedModelNeeded",
      "placementTag",
    ],
    "complete-denture": ["teethReplaced", "occlusionType", "desiredOutput"],
    "partial-denture": ["teethReplaced", "desiredOutput"],
    "partial-cast": ["frameworkMaterial", "desiredArch"],
    surgicalGuide: [
      "dicomFile",
      "stlFile",
      "plannedImplantSites",
      "plannedImplantManufacturer",
      "guideType",
    ],
    "nightguard-bite-splint": ["neededFor", "margin", "thickness"],
    "digital-model": ["neededFor", "modelType"],
    otherTreatments: ["requirement"],
    filledOrder: [],
  };

  // 👇 Define per-instance rules for services that have instanceDetails array
  const REQUIRED_INSTANCE_FIELDS: Record<string, string[]> = {
    crownAndBridge: [
      "toothNumbers",
      "material",
      "prosthesisType",
      "designType",
    ],
    Implant: [
      "toothNumbers",
      "material",
      "scanBodyManufacturer",
      "implantManufacturer",
      "platformDiameter",
      "restorationRequired",
      "prosthesisType",
    ],
  };

  // 👇 JU-specific required fields for Implant
  const REQUIRED_INSTANCE_FIELDS_JU_IMPLANT: string[] = [
    "toothNumbers",
    "restorationRequired",
    "prosthesisType",
    "implantSystem",
    "implantSize",
    "connectionType",
    "scanFlagType",
  ];

  const validateRequiredFields = (): boolean => {
    const { case_owner } = formData;

    // Step 1: global validation
    if (!case_owner?.trim()) {
      console.warn("Missing required field: patient name");
      return false;
    }

    if (
      (isVelvetLabToken(user?.token) || isVelvetLabToken(formData.upload_token)) &&
      !casePriority?.trim()
    ) {
      console.warn("Missing required field: Case Priority");
      return false;
    }

    if (user?.partner === "bant" && !previewNeeded?.trim()) {
      console.warn("Missing required field: Model Preview Needed");
      return false;
    }

    if (!selectedServices || selectedServices.length === 0) {
      console.warn("At least one service must be selected");
      return false;
    }

    // Step 2: service-wise validation
    for (const service of selectedServices) {
      if (service === "filledOrder") continue;

      const requiredFields = REQUIRED_FIELDS_BY_SERVICE[service] || [];
      let serviceData: Record<string, any> = {};

      switch (service) {
        case "crownAndBridge":
          serviceData = crownBridgeData || {};
          break;
        case "Implant":
          serviceData = implantData || {};
          break;
        case "smileDesign":
          serviceData = smileDesignData || {};
          break;
        case "veneers":
          serviceData = veneersData || {};
          break;
        case "complete-denture":
          serviceData = completeDentureData || {};
          break;
        case "partial-denture":
          serviceData = partialDentureData || {};
          break;
        case "partial-cast":
          serviceData = partialCastData || {};
          break;
        case "surgicalGuide":
          serviceData = surgicalGuideData || {};
          break;
        case "nightguard-bite-splint":
          serviceData = nightGuardData || {};
          break;
        case "digital-model":
          serviceData = digitalModelData || {};
          break;
        case "otherTreatments":
          serviceData = otherTreatmentsData || {};
          break;
        default:
          break;
      }

      // Step 3: top-level field validation
      for (const field of requiredFields) {
        const value = serviceData[field];
        if (
          value === undefined ||
          value === null ||
          (typeof value === "string" && !value.trim()) ||
          (Array.isArray(value) && value.length === 0)
        ) {
          console.warn(
            `Missing required field "${field}" for service "${service}"`
          );
          return false;
        }
      }

      // Step 4: special case – validate inside instanceDetails arrays
      if (["crownAndBridge", "Implant"].includes(service)) {
        const instances = serviceData.instanceDetails;
        // const instanceRequiredFields = REQUIRED_INSTANCE_FIELDS[service] || [];

        // For Implant service, check if token is JU to use different required fields
        let instanceRequiredFields: string[] = [];
        if (
          service === "Implant" &&
          (isVelvetLabToken(user?.token) || isVelvetLabToken(formData.upload_token))
        ) {
          instanceRequiredFields = REQUIRED_INSTANCE_FIELDS_JU_IMPLANT;
        } else {
          instanceRequiredFields = REQUIRED_INSTANCE_FIELDS[service] || [];
        }
        if (!Array.isArray(instances) || instances.length === 0) {
          console.warn(`No instance details found for service "${service}"`);
          return false;
        }

        for (let i = 0; i < instances.length; i++) {
          const instance = instances[i];
          for (const field of instanceRequiredFields) {
            const val = instance[field];
            if (
              val === undefined ||
              val === null ||
              (typeof val === "string" && !val.trim()) ||
              (Array.isArray(val) && val.length === 0)
            ) {
              console.warn(
                `Missing required field "${field}" in instance ${i + 1
                } of "${service}"`
              );
              return false;
            }
          }
        }
      }

      // Step 5: conditional rule for surgical guide
      if (service === "surgicalGuide") {
        const guideType = serviceData?.guideType;
        if (guideType === "Pilot-drill only") {
          const pilotDrillSize = serviceData?.pilotDrillSize;
          if (!pilotDrillSize?.trim()) {
            console.warn(
              `Missing required field "pilotDrillSize" because guideType is "Pilot-drill only"`
            );
            return false;
          }
        }
      }
    }

    return true;
  };

  const isFormComplete = validateRequiredFields();
  const canSubmit = !isSubmitting;
  const isJuToken =
    isVelvetLabToken(user?.token) || isVelvetLabToken(formData.upload_token);
  const hideLabToken =
    user?.role?.toLowerCase() === "user" ||
    user?.role?.toLowerCase() === "side admin";
  const showCasePriority =
    isJuToken ||
    ["admin", "operations", "super admin"].includes(user?.role?.toLowerCase() || "");
  const showPreviewNeeded =
    user?.partner === "bant" ||
    ["admin", "operations", "super admin"].includes(user?.role?.toLowerCase() || "");
  const servicesInvalid =
    showValidationErrors &&
    (!selectedServices || selectedServices.length === 0);
  const hasServicePanels =
    selectedServices.some((s) => s !== "filledOrder") && !orderFormConfirmed;

  return (
    <>
      {!embedded && (
        <Helmet>
          <title>Upload Case</title>
        </Helmet>
      )}

      <div
        className="case-upload-theme w-full overflow-hidden rounded-2xl border border-[var(--cu-border)] bg-[var(--cu-bg)]"
        data-theme={formTheme}
        data-brand={brand}
      >
        <FormValidationProvider value={{ showErrors: showValidationErrors }}>
        <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown}>
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="flex flex-col gap-3 border-b border-[var(--cu-border)] px-4 py-4 md:flex-row md:items-center md:justify-between md:px-6"
          >
            <div>
              <p className="text-xs text-[var(--cu-subtle)]">
                {brand === "uris" ? "Uris Dental" : "Step 1 of 2"}
              </p>
              <h2 className="mt-0.5 text-xl font-semibold tracking-tight text-[var(--cu-heading)]">
                {brand === "uris" ? "Upload case" : "Case Details"}
              </h2>
            </div>

            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 text-xs">
                <span className="rounded-full bg-[var(--cu-accent-soft)] px-2.5 py-1 font-medium text-[var(--cu-accent)]">
                  1 · Details
                </span>
                <span className="text-[var(--cu-border-hover)]">→</span>
                <span className="rounded-full bg-[var(--cu-chip)] px-2.5 py-1 text-[var(--cu-subtle)]">
                  {brand === "uris" ? "2 · Files on this page" : "2 · Files"}
                </span>
              </div>

              {!forceTheme && (
              <button
                type="button"
                onClick={toggleFormTheme}
                aria-label={
                  formTheme === "dark"
                    ? "Switch to light mode"
                    : "Switch to dark mode"
                }
                title={
                  formTheme === "dark"
                    ? "Switch to light mode"
                    : "Switch to dark mode"
                }
                className="ml-1 inline-flex h-9 items-center gap-2 rounded-lg border border-[var(--cu-border)] bg-[var(--cu-surface)] px-3 text-xs font-medium text-[var(--cu-muted)] transition-colors hover:border-[var(--cu-border-hover)] hover:text-[var(--cu-text)]"
              >
                {formTheme === "dark" ? (
                  <>
                    <FiSun className="h-3.5 w-3.5 text-[var(--cu-accent)]" />
                    Light
                  </>
                ) : (
                  <>
                    <FiMoon className="h-3.5 w-3.5 text-[var(--cu-accent)]" />
                    Dark
                  </>
                )}
              </button>
              )}
            </div>
          </motion.div>

          <motion.div
            variants={formContainer}
            initial="hidden"
            animate="visible"
            className="space-y-4 px-4 py-4 md:px-6 md:py-5"
          >
            <motion.section
              variants={formSection}
              className="rounded-xl border border-[var(--cu-border)] bg-[var(--cu-surface)] p-4"
            >
              <div className="mb-4 flex items-center gap-2">
                <FiFileText className="h-4 w-4 text-[var(--cu-accent)]" />
                <h3 className="text-sm font-medium text-[var(--cu-heading)]">
                  Case Information
                </h3>
              </div>

              <div className="space-y-3">
              {!hideLabToken && !embedded && (
                <>
                  <TextInput
                    label="Lab Token"
                    name="upload_token"
                    value={formData.upload_token}
                    onChange={handleInputChange}
                    optional
                  />
                  {isFzToken(formData.upload_token) || isFzToken(user?.token) ? (
                    <p className="mt-1 text-xs text-amber-700">
                      FZ cases cannot be created here — use Local Downloader →
                      FZ Sync (zip names only, no file upload).
                    </p>
                  ) : null}
                </>
              )}

              <TextInput
                label="Patient Name / Job Number"
                name="case_owner"
                value={formData.case_owner}
                onChange={handleInputChange}
              />

              {!lockedServiceKey && (
              <div
                className={`rounded-xl border p-3.5 ${
                  servicesInvalid
                    ? "border-[var(--cu-danger)] bg-[var(--cu-danger)]/5 ring-1 ring-[var(--cu-danger)]/40"
                    : "border-[var(--cu-border)] bg-[var(--cu-elevated)]"
                }`}
                data-invalid={servicesInvalid || undefined}
              >
                <div className="mb-2.5 flex items-center justify-between gap-3">
                  <label
                    className={`text-xs font-medium ${
                      servicesInvalid
                        ? "text-[var(--cu-danger)]"
                        : "text-[var(--cu-muted)]"
                    }`}
                  >
                    Services requested
                    <span className="ml-1 text-[var(--cu-danger)]">*</span>
                    {servicesInvalid && (
                      <span className="ml-1.5 text-[11px] font-normal">
                        Select at least one
                      </span>
                    )}
                  </label>
                  {selectedServices.length > 0 && (
                    <span className="text-xs text-[var(--cu-accent)]">
                      {orderFormConfirmed
                        ? "Filled order"
                        : `${selectedServices.length} selected`}
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 xl:grid-cols-6">
                  {services.map((service) => {
                    const key = serviceMap[service];
                    const isFilledOrder = key === "filledOrder";
                    const checked = lockedServiceKey
                      ? key === lockedServiceKey
                      : isFilledOrder
                        ? orderFormConfirmed
                        : selectedServices.includes(key);
                    const disabled = lockedServiceKey
                      ? key !== lockedServiceKey
                      : (orderFormConfirmed && !isFilledOrder) ||
                        (!orderFormConfirmed &&
                          isFilledOrder &&
                          selectedServices.length > 0);
                    const icon = SERVICE_ICONS[service] ?? (
                      <FiFileText className="h-6 w-6" />
                    );

                    return (
                      <button
                        key={service}
                        type="button"
                        title={service}
                        aria-label={service}
                        onClick={() => {
                          if (!disabled) handleCheckboxChange(service);
                        }}
                        disabled={disabled}
                        aria-pressed={checked}
                        className={`group relative flex flex-col items-center justify-center gap-1.5 rounded-xl border px-2 py-3 transition-colors ${
                          checked
                            ? "border-[var(--cu-accent)] bg-[var(--cu-accent-soft)] text-[var(--cu-accent)]"
                            : disabled
                              ? "cursor-not-allowed border-[var(--cu-border)] bg-[var(--cu-chip)] text-[var(--cu-disabled-text)] opacity-45"
                              : "border-[var(--cu-border)] bg-[var(--cu-chip)] text-[var(--cu-muted)] hover:border-[var(--cu-border-hover)] hover:text-[var(--cu-text)]"
                        }`}
                      >
                        {checked && (
                          <span className="absolute right-1.5 top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-[var(--cu-accent)] text-white">
                            <FiCheck className="h-2.5 w-2.5" strokeWidth={3} />
                          </span>
                        )}
                        <span
                          className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                            checked
                              ? "bg-[var(--cu-accent)] text-white"
                              : "bg-[var(--cu-input)] text-inherit"
                          }`}
                        >
                          {icon}
                        </span>
                        <span className="line-clamp-2 max-w-full text-center text-[9px] font-medium leading-tight opacity-80 group-hover:opacity-100">
                          {service.split(" / ")[0].replace("LDP ", "")}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
              )}

              {(showCasePriority || showPreviewNeeded) && (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  {showCasePriority && (
                    <SelectInput
                      stacked
                      label="Case Priority"
                      name="casePriority"
                      value={casePriority}
                      optional={!isJuToken}
                      onChange={(e) => setCasePriority(e.target.value)}
                      options={[
                        {
                          value: "High priority(Rush case)",
                          label: "High priority(Rush case)",
                        },
                        {
                          value: "Medium priority (Regular case)",
                          label: "Medium priority (Regular case)",
                        },
                        { value: "Low priority", label: "Low priority" },
                      ]}
                    />
                  )}

                  {showPreviewNeeded && (
                    <SelectInput
                      stacked
                      label="Is model preview needed?"
                      name="previewNeeded"
                      value={previewNeeded}
                      optional={user?.partner !== "bant"}
                      onChange={(e) => setPreviewNeeded(e.target.value)}
                      options={[
                        { value: "Yes", label: "Yes" },
                        { value: "No", label: "No" },
                      ]}
                    />
                  )}
                </div>
              )}
              </div>
            </motion.section>

            <AnimatePresence mode="sync">
              {hasServicePanels && (
                <motion.section
                  key="service-panels"
                  variants={formSection}
                  initial="hidden"
                  animate="visible"
                  exit={{ opacity: 0, y: -8 }}
                  className="space-y-3"
                >
                  <p className="px-0.5 text-xs text-[var(--cu-subtle)]">
                    Clinical specifications
                  </p>

                  <AnimatePresence initial={false}>
                    {selectedServices.includes("crownAndBridge") && (
                      <motion.div
                        key="crownAndBridge"
                        {...servicePanel}
                        className="rounded-xl border border-[var(--cu-border)] bg-[var(--cu-surface)] p-4"
                      >
                        <CrownBridge
                          filterToken={
                            formData?.upload_token || user?.token || ""
                          }
                          onChange={setCrownBridgeData}
                        />
                      </motion.div>
                    )}
                    {selectedServices.includes("Implant") && (
                      <motion.div
                        key="Implant"
                        {...servicePanel}
                        className="rounded-xl border border-[var(--cu-border)] bg-[var(--cu-surface)] p-4"
                      >
                        <Implant
                          filterToken={
                            formData?.upload_token || user?.token || ""
                          }
                          onChange={setImplantData}
                        />
                      </motion.div>
                    )}
                    {selectedServices.includes("smileDesign") && (
                      <motion.div
                        key="smileDesign"
                        {...servicePanel}
                        className="rounded-xl border border-[var(--cu-border)] bg-[var(--cu-surface)] p-4"
                      >
                        <SmileDesign
                          onChange={setSmileDesignData}
                          hidePhotoIncluded={embedded}
                        />
                      </motion.div>
                    )}
                    {selectedServices.includes("veneers") && (
                      <motion.div
                        key="veneers"
                        {...servicePanel}
                        className="rounded-xl border border-[var(--cu-border)] bg-[var(--cu-surface)] p-4"
                      >
                        <Veneers onChange={setVeneersData} />
                      </motion.div>
                    )}
                    {selectedServices.includes("complete-denture") && (
                      <motion.div
                        key="complete-denture"
                        {...servicePanel}
                        className="rounded-xl border border-[var(--cu-border)] bg-[var(--cu-surface)] p-4"
                      >
                        <CompleteDenture onChange={setcompleteDentureData} />
                      </motion.div>
                    )}
                    {selectedServices.includes("partial-denture") && (
                      <motion.div
                        key="partial-denture"
                        {...servicePanel}
                        className="rounded-xl border border-[var(--cu-border)] bg-[var(--cu-surface)] p-4"
                      >
                        <PartialDenture onChange={setpartialDentureData} />
                      </motion.div>
                    )}
                    {selectedServices.includes("partial-cast") && (
                      <motion.div
                        key="partial-cast"
                        {...servicePanel}
                        className="rounded-xl border border-[var(--cu-border)] bg-[var(--cu-surface)] p-4"
                      >
                        <PartialCast onChange={setpartialCastData} />
                      </motion.div>
                    )}
                    {selectedServices.includes("surgicalGuide") && (
                      <motion.div
                        key="surgicalGuide"
                        {...servicePanel}
                        className="rounded-xl border border-[var(--cu-border)] bg-[var(--cu-surface)] p-4"
                      >
                        <SurgicalGuide onChange={setSurgicalGuideData} />
                      </motion.div>
                    )}
                    {selectedServices.includes("nightguard-bite-splint") && (
                      <motion.div
                        key="nightguard-bite-splint"
                        {...servicePanel}
                        className="rounded-xl border border-[var(--cu-border)] bg-[var(--cu-surface)] p-4"
                      >
                        <NightGuard onChange={setNightGuardData} />
                      </motion.div>
                    )}
                    {selectedServices.includes("digital-model") && (
                      <motion.div
                        key="digital-model"
                        {...servicePanel}
                        className="rounded-xl border border-[var(--cu-border)] bg-[var(--cu-surface)] p-4"
                      >
                        <DigitalModel onChange={setDigitalModelData} />
                      </motion.div>
                    )}
                    {selectedServices.includes("otherTreatments") && (
                      <motion.div
                        key="otherTreatments"
                        {...servicePanel}
                        className="rounded-xl border border-[var(--cu-border)] bg-[var(--cu-surface)] p-4"
                      >
                        <OtherTreatments onChange={setOtherTreatmentsData} />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.section>
              )}
            </AnimatePresence>

            <motion.section
              variants={formSection}
              className="rounded-xl border border-[var(--cu-border)] bg-[var(--cu-surface)] p-4"
            >
              <TextInput
                label="Additional notes"
                name="additionalNote"
                value={additionalNote}
                onChange={(e) => setAdditionalNote(e.target.value)}
                optional
              />

              <div className="mt-2 flex flex-col gap-3 border-t border-[var(--cu-border)] pt-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0 flex-1">
                  <p
                    className={`text-xs font-medium ${
                      isSubmitting
                        ? "text-[var(--cu-subtle)]"
                        : isFormComplete
                          ? "text-[var(--cu-accent)]"
                          : showValidationErrors
                            ? "text-[var(--cu-danger)]"
                            : "text-[var(--cu-subtle)]"
                    }`}
                  >
                    {isSubmitting
                      ? "Uploading case details…"
                      : isFormComplete
                        ? "All required fields complete — ready to continue."
                        : showValidationErrors
                          ? "Required fields are highlighted in red."
                          : "Fill all required fields to unlock continue."}
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={!canSubmit}
                  aria-disabled={!isFormComplete && !isSubmitting}
                  className={`inline-flex min-w-[12.5rem] items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold transition-all duration-300 ${
                    isSubmitting
                      ? "cursor-wait bg-[var(--cu-accent)]/70 text-white"
                      : isFormComplete
                        ? "bg-[var(--cu-accent)] text-white shadow-[0_8px_24px_-8px_rgba(47,169,191,0.65)] hover:brightness-110 hover:shadow-[0_10px_28px_-8px_rgba(47,169,191,0.75)] active:scale-[0.98]"
                        : "border border-dashed border-[var(--cu-border-hover)] bg-[var(--cu-elevated)] text-[var(--cu-muted)] hover:border-[var(--cu-danger)]/50 hover:bg-[var(--cu-danger)]/5 hover:text-[var(--cu-danger)]"
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                      Uploading…
                    </>
                  ) : isFormComplete ? (
                    <>
                      <FiCheck className="h-4 w-4" strokeWidth={2.5} />
                      {brand === "uris" ? "Save case" : "Continue to File Upload"}
                      <FiArrowRight className="h-4 w-4" />
                    </>
                  ) : (
                    <>
                      <FiAlertCircle className="h-4 w-4" />
                      Complete required fields
                    </>
                  )}
                </button>
              </div>
            </motion.section>
          </motion.div>
        </form>
        </FormValidationProvider>
      </div>
    </>
  );
};

export default CaseDetailsForm;
